import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, filter, first, skip, Subject, tap } from 'rxjs';
import { Socket } from 'ngx-socket-io';
import { TokenStoreService } from './token-store.service';
import { AuthService, ParsedToken, ParsedUser } from './auth.service';
import { AppStateChangeService } from './app-state-change.service';
import {
  ChatPresenceEvent,
  ChatPresenceState,
  ChatPresenceUpdate,
  ChatUnreadUpdatedSocketEvent,
  ChatTypingEvent,
  ChatTypingSnapshot,
  ChatTypingUpdate,
  EmitChatTyping,
  EmitChatTypingView,
  EmitMessage,
  EmitRead,
  EventAnswer,
  EventAvatarCustomerReady,
  EventAvatarCustomerReadyReconnect,
  EventAvatarRequestReconnect,
  EventAvatarResponse,
  EventAvatarStatusUpdated,
  EventAvatarStreamerReady,
  EventAvatarSyncStatus,
  EventAvatarViewerReady,
  EventChatCreated,
  EventExit,
  EventExitFromBackend,
  EventFileUploaded,
  EventPartnerConnectedStatus,
  EventRead,
  EventStreamBanApplied,
  EventStreamLikes,
  EventStreamLive,
  EventStreamLiveViewers,
  EventStreamMessage,
  EventStreamNoticed,
  EventStreamWatches,
  EventWalletTransferUpdated,
  FileUploadEvent,
  PhoneInfoRequest,
  PhoneInfoRequestExt,
  PhoneInfoResponse,
  PhoneInfoResponseExt,
  AudioCallDeliveryReceipt,
  AudioCallDeliveryStatus,
  AudioCallReliableEnvelope,
  AudioCallReliableEventName,
  AudioCallReliableMeta,
  AudioCallReliableSendResult,
  SOCKET_EVENT_NAMES,
  SocketInitPayload,
  SocketReplacedEvent,
  SocketDeliveryAck,
  SocketError,
  StreamBanUserInfo
} from './web-socket.interfaces';
import {
  EventAudioCallCustomerReady,
  EventAudioCallCustomerReadyReconnect,
  EventAudioCallExit,
  EventAudioCallExitFromBackend,
  EventAudioCallPartnerConnectedStatus,
  EventAudioCallRequestReconnect,
  EventAudioCallResponse,
  EventAudioCallStreamerReady,
} from './audio-call.socket.interfaces';
import { distinctUntilChanged } from 'rxjs/operators';
import { PlatformService } from './platform.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { OnlineStateService } from './online-state.service';
import { JwtService } from './jwt.service';
import { AvatarOrderResponse } from '@api/client';
import { environment } from '@environments/environment';
import { AudioCallConnectionDebugService } from './audio-call-connection-debug.service';
import { DeviceIdentityService, SocketDeviceIdentity } from './device-identity.service';

interface CachedAudioCallReceiptEntry {
  receipt: AudioCallDeliveryReceipt;
  expiresAt: number;
  receivedAt: number;
}

interface PendingAudioCallWebrtcStatus {
  order_id: number;
  is_connected: boolean;
}

type SocketInitPayloadMode = 'structured' | 'legacy-probe' | 'legacy';

@Injectable({
  providedIn: 'root'
})
export class WebSocketController {
  private readonly socket = inject(Socket);
  private readonly tokenStore = inject(TokenStoreService);
  private readonly authService = inject(AuthService);
  private readonly appStateChangeService = inject(AppStateChangeService);
  private readonly platformService = inject(PlatformService);
  private readonly onlineStateService = inject(OnlineStateService);
  private readonly jwtService = inject(JwtService);
  private readonly audioCallConnectionDebugService = inject(AudioCallConnectionDebugService);
  private readonly deviceIdentityService: DeviceIdentityService = inject(DeviceIdentityService);

  public chatEvent: Subject<any> = new Subject();
  public notificationEvent: Subject<any> = new Subject();
  public avatarEvent: Subject<any> = new Subject();
  public audioCallEvent: Subject<any> = new Subject();
  public streamEvent: Subject<any> = new Subject();
  public fileUploadEvent: Subject<FileUploadEvent> = new Subject<FileUploadEvent>();
  public chatPresenceEvent: Subject<ChatPresenceEvent> = new Subject<ChatPresenceEvent>();
  public chatTypingEvent: Subject<ChatTypingEvent> = new Subject<ChatTypingEvent>();
  public walletTransferEvent: Subject<EventWalletTransferUpdated> = new Subject<EventWalletTransferUpdated>();
  public isSocketInitedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isSocketConnectSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private sb: SubscriptionsBag = new SubscriptionsBag();
  private readonly ackTimeoutMs: number = 1200;
  private readonly deliveryReceiptTimeoutMs: number = 1500;
  private readonly reliableEventRetryDelayMs: number = 250;
  private readonly reliableEventMaxAttempts: number = 5;
  private readonly receiptCacheTtlMs: number = 120000;
  private readonly socketInitAnswerTimeoutMs: number = 8000;
  private readonly socketInitAuthRefreshRetryDelayMs: number = 5000;
  private readonly socketInitMaxAuthRefreshAttempts: number = 2;
  private readonly socketInitReconnectDelayMs: number = 1000;
  private readonly pendingAudioCallCandidateTtlMs: number = 6000;
  private readonly maxPendingAudioCallCandidates: number = 120;
  private readonly maxProcessedWalletTransferEvents: number = 100;
  private pendingAudioCallCandidates: Array<{ to_id: number; candidate: RTCIceCandidateInit; queuedAt: number; message_id: string }> = [];
  private pendingAudioCallWebrtcStatus: PendingAudioCallWebrtcStatus | null = null;
  private pendingAudioCallReceiptResolvers: Map<string, Array<(receipt: AudioCallDeliveryReceipt) => void>> = new Map();
  private cachedAudioCallReceipts: Map<string, CachedAudioCallReceiptEntry> = new Map();
  private processedWalletTransferEventIds: Set<string> = new Set<string>();
  private socketInitAttemptedSocketId: string | null = null;
  private socketInitJwtToken: string | null = null;
  private socketAuthRevision: number = 0;
  private socketInitAttemptAuthRevision: number = 0;
  private socketInitPending: boolean = false;
  private socketInitAnswerTimeout: ReturnType<typeof setTimeout> | null = null;
  private socketInitReconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private socketInitAuthRefreshPromise: Promise<void> | null = null;
  private socketInitAuthRefreshAttempts: number = 0;
  private socketInitPayloadMode: SocketInitPayloadMode = 'structured';
  private lastSocketInitAt: number = 0;
  private socketConnectionRequested: boolean = false;
  private socketWasReplaced: boolean = false;

  constructor() {
    /* this.sb.sub = this.isSocketConnectSubject.pipe(
      skip(1),
      distinctUntilChanged(),
    ).subscribe((isConnected: boolean): void => {
      this.onlineStateService.isOffline = !isConnected;
    }); */
  }

  ngOnDestroy(): void {
    this.clearAudioCallReceiptState();
    this.clearSocketInitAnswerTimeout();
    this.clearSocketInitReconnectTimeout();
    this.pendingAudioCallCandidates = [];
    this.pendingAudioCallWebrtcStatus = null;
    this.socketInitAuthRefreshPromise = null;
    this.lastSocketInitAt = 0;
    this.socketConnectionRequested = false;
    this.sb.unsubscribeAll();
  }

  public get socketId(): string {
    return this.socket.ioSocket.id;
  }

  public get socketConnected(): boolean {
    return this.socket.ioSocket.connected;
  }

  public async init(): Promise<void> {
    if (this.onlineStateService.isOffline) {
      this.sb.sub = this.onlineStateService.isOffline$.pipe(
        first((isOffline: boolean): boolean => !isOffline),
      ).subscribe(async (): Promise<void> => {
        await this.init();
      });
      return;
    }

    this.subscribeToSocketEvents();

    this.sb.sub = this.authService.parsedUser$.pipe(
      distinctUntilChanged(),
      tap((parsedUser: ParsedUser | null): void => {
        if (parsedUser) {
          this.socketConnect();
        } else {
          this.socketWasReplaced = false;
          this.socketDisconnect();
        }
      })
    ).subscribe();

    this.sb.sub = this.authService.parsedToken$.pipe(
      distinctUntilChanged(),
      filter((parsedToken: ParsedToken | null): parsedToken is ParsedToken => !!parsedToken),
      tap((): void => {
        if (!this.socketInitAuthRefreshPromise) {
          this.socketInitAuthRefreshAttempts = 0;
        }
        this.socketAuthRevision++;
        this.socketConnect();
      })
    ).subscribe();

    this.sb.sub = this.appStateChangeService.appStateChange$.pipe(
      filter((): boolean => this.platformService.isDevice),
      filter((isActive: boolean): boolean => isActive),
      skip(1),
      tap((): void => {
        if (this.authService.isAuthenticated || this.socketConnectionRequested) {
          this.socketConnect();
        }
      })
    ).subscribe();

    this.sb.sub = this.onlineStateService.isOffline$.pipe(
      skip(1),
      filter((isOffline: boolean): boolean => !isOffline),
    ).subscribe((): void => {
      if (this.authService.isAuthenticated || this.socketConnectionRequested) {
        this.socketConnect();
      }
    });
  }

  //#region Socket Emits

  public emitAnySocketEvent(eventName: SOCKET_EVENT_NAMES, eventData: any): void {
    this.validateEmitData({...eventData}, eventName);
    // console.log('\x1b[35m' + `Socket emit '${eventName}'` + '\x1b[0m', eventData);
    this.socket.emit(eventName, eventData);
  }

  public emitInit(jwtToken: string): void {
    if (this.socketInitPayloadMode !== 'structured') {
      this.emitLegacySocketInit(jwtToken);
      return;
    }

    const identity: SocketDeviceIdentity = this.deviceIdentityService.getIdentity();
    const payload: SocketInitPayload = {
      token: jwtToken,
      device_id: identity.device_id,
      app_session_id: identity.app_session_id,
    };
    this.validateEmitData(payload, SOCKET_EVENT_NAMES.INIT);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.INIT}'` + '\x1b[0m', jwtToken);
    this.socket.emit(SOCKET_EVENT_NAMES.INIT, payload);
  }

  public canEmitMessage(): boolean {
    return this.socketConnected && this.isSocketInitedSubject.value;
  }

  public emitMessage(data: EmitMessage): boolean {
    if (!this.canEmitMessage()) {
      return false;
    }

    const payload: EmitMessage = {
      chat_id: data.chat_id,
      to_id: data.to_id ?? null,
      message: data.message ?? '',
      file_ids: Array.isArray(data.file_ids) ? [...data.file_ids] : [],
      guid: data.guid,
      reply_message_id: data.reply_message_id ?? null,
    };
    if ('data' in data) {
      payload.data = data.data ?? null;
    }

    this.validateEmitData(payload, SOCKET_EVENT_NAMES.MESSAGE);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.MESSAGE}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.MESSAGE, payload);
    return true;
  }

  public emitGetHistory(
    message_id: string,
    to_id: number,
    count: number,
    chat_id: number,
    skip: number = 0,
    request_id?: string,
    options?: {
      around?: boolean;
      anchor_mode?: string;
      anchor_id?: string;
      joined_at?: string;
      before?: number;
      after?: number;
      before_id?: string;
      after_id?: string;
    }
  ): void {
    const data: {
      message_id: string;
      to_id: number;
      count: number;
      chat_id: number;
      skip: number;
      request_id?: string;
      around?: boolean;
      anchor_mode?: string;
      anchor_id?: string;
      joined_at?: string;
      before?: number;
      after?: number;
      before_id?: string;
      after_id?: string;
    } = {
      message_id,
      to_id,
      count,
      chat_id,
      skip,
    };
    if (request_id) {
      data.request_id = request_id;
    }
    if (options) {
      if (options.around !== undefined) data.around = options.around;
      if (options.anchor_mode) data.anchor_mode = options.anchor_mode;
      if (options.anchor_id) data.anchor_id = options.anchor_id;
      if (options.joined_at) data.joined_at = options.joined_at;
      if (typeof options.before === 'number') data.before = options.before;
      if (typeof options.after === 'number') data.after = options.after;
      if (options.before_id) data.before_id = options.before_id;
      if (options.after_id) data.after_id = options.after_id;
    }
    this.validateEmitData(data, SOCKET_EVENT_NAMES.GET_HISTORY);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.GET_HISTORY}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.GET_HISTORY, data);
  }

  public emitRead(data: EmitRead): void {
    this.validateEmitData(data, SOCKET_EVENT_NAMES.READ);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.READ}'` + '\x1b[0m', data); read: ', data);
    this.socket.emit(SOCKET_EVENT_NAMES.READ, data);
  }

  public emitReaders(chat_id: number, message_id: string): void {
    const data = {chat_id, message_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.READERS);
    this.socket.emit(SOCKET_EVENT_NAMES.READERS, data);
  }

  public emitAvatarCustomerReady(to_id: number, order_id: number): void {
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY, data);
  }

  public async emitAudioCallCustomerReadyWithAck(to_id: number, order_id: number, message_id?: string): Promise<AudioCallReliableSendResult> {
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY);
    return await this.emitAudioCallReliableEvent(
      SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY,
      data,
      {
        attempts: 1,
        messageId: message_id,
        delayMs: this.reliableEventRetryDelayMs,
        ignoredReceiptRetryable: true,
      },
    );
  }

  public emitAvatarMessage(to_id: number, message: string): void {
    const data = {to_id, message};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_MESSAGE);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_MESSAGE}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_MESSAGE, data);
  }

  public emitAvatarOffer(customer_id: number, order_id: number, offer: RTCSessionDescriptionInit): void {
    const data = {to_id: customer_id, order_id, offer};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_OFFER);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_OFFER}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_OFFER, data);
  }

  public async emitAudioCallOffer(customer_id: number, order_id: number, offer: RTCSessionDescriptionInit): Promise<AudioCallReliableSendResult> {
    const data = {to_id: customer_id, order_id, offer};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER);
    return await this.emitAudioCallReliableEvent(
      SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER,
      data,
      {
        attempts: this.reliableEventMaxAttempts,
        delayMs: this.reliableEventRetryDelayMs,
        receiptTimeoutMs: this.deliveryReceiptTimeoutMs,
        ignoredReceiptRetryable: true,
      },
    );
  }

  public emitAvatarAnswer(executor_id: number, answer: RTCSessionDescriptionInit): void {
    const data = {to_id: executor_id, answer};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_ANSWER);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_ANSWER}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_ANSWER, data);
  }

  public async emitAudioCallAnswer(executor_id: number, answer: RTCSessionDescriptionInit, order_id?: number): Promise<AudioCallReliableSendResult> {
    const data = {to_id: executor_id, answer, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER);
    return await this.emitAudioCallReliableEvent(
      SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER,
      data,
      {
        attempts: this.reliableEventMaxAttempts,
        delayMs: this.reliableEventRetryDelayMs,
        receiptTimeoutMs: this.deliveryReceiptTimeoutMs,
        ignoredReceiptRetryable: true,
      },
    );
  }

  public emitAvatarCandidate(to_id: number, candidate: RTCIceCandidateInit): void {
    const data = {to_id, candidate};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_CANDIDATE);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_CANDIDATE}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_CANDIDATE, data);
  }

  public emitAudioCallCandidate(to_id: number, candidate: RTCIceCandidateInit, order_id?: number): void {
    const normalizedCandidate: RTCIceCandidateInit = {...candidate};
    if (typeof order_id === 'number' && Number.isFinite(order_id)) {
      Reflect.set(normalizedCandidate, 'order_id', order_id);
    }
    const data = {to_id, candidate: normalizedCandidate};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE);
    const messageId: string = this.createReliableMessageId();

    if (!this.socketConnected || !this.isSocketInitedSubject.value) {
      this.enqueuePendingAudioCallCandidate(to_id, normalizedCandidate, messageId);
      return;
    }

    this.flushPendingAudioCallCandidates();
    this.emitQueuedAudioCallCandidateReliably(to_id, normalizedCandidate, messageId);
  }

  public emitAvatarExit(to_id: number): void { // order time stop
    const data = {to_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_EXIT);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_EXIT}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_EXIT, data);
  }

  public async emitAudioCallExit(to_id: number, order_id: number): Promise<AudioCallReliableSendResult> {
    if (!to_id) {
      console.error('Partner ID is not defined for audio call exit!');
      return this.buildReliableSendResult(
        '',
        null,
        null,
        false,
        false,
        'invalid_payload',
        'Partner ID is not defined for audio call exit!',
      );
    }
    if (!order_id) {
      console.error('Order ID is not defined for audio call exit!');
      return this.buildReliableSendResult(
        '',
        null,
        null,
        false,
        false,
        'invalid_payload',
        'Order ID is not defined for audio call exit!',
      );
    }
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT);
    return await this.emitAudioCallReliableEvent(
      SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT,
      data,
      {
        attempts: this.reliableEventMaxAttempts,
        delayMs: this.reliableEventRetryDelayMs,
        receiptTimeoutMs: this.deliveryReceiptTimeoutMs,
      },
    );
  }

  public emitAvatarPhoneInfoRequest(to_id: number): void {
    const data: PhoneInfoRequest = {to_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST, data);
  }

  public emitAvatarPhoneInfoResponse(to_id: number, battery_level: number, is_charging: boolean, phone_model: string): void {
    const data: PhoneInfoResponse = {to_id, battery_level, is_charging, phone_model};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_RESPONSE);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_RESPONSE}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_RESPONSE, data);
  }

  public emitAvatarSyncStatus(is_online: boolean): void {
    const data = {is_online};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS, data);
  }

  public emitChatPresenceActivity(is_active: boolean): void {
    const data = {is_active};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.CHAT_PRESENCE_ACTIVITY);
    this.socket.emit(SOCKET_EVENT_NAMES.CHAT_PRESENCE_ACTIVITY, data);
  }

  public emitChatTyping(chat_id: number, is_typing: boolean): void {
    const data: EmitChatTyping = {chat_id, is_typing};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.CHAT_TYPING);
    this.socket.emit(SOCKET_EVENT_NAMES.CHAT_TYPING, data);
  }

  public emitChatTypingView(chat_id: number, is_open: boolean): void {
    const data: EmitChatTypingView = {chat_id, is_open};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.CHAT_TYPING_VIEW);
    this.socket.emit(SOCKET_EVENT_NAMES.CHAT_TYPING_VIEW, data);
  }

  public emitAvatarRequestReconnect(to_id: number, order_id: number): void {
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_REQUEST_RECONNECT);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_REQUEST_RECONNECT}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_REQUEST_RECONNECT, data);
  }

  public async emitAudioCallRequestReconnect(to_id: number, order_id: number): Promise<AudioCallReliableSendResult> {
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT);
    return await this.emitAudioCallReliableEvent(
      SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT,
      data,
      {
        attempts: this.reliableEventMaxAttempts,
        delayMs: this.reliableEventRetryDelayMs,
        receiptTimeoutMs: this.deliveryReceiptTimeoutMs,
      },
    );
  }

  public emitAvatarCustomerReadyReconnect(to_id: number, ready_reconnect: boolean, order_id: number): void {
    const data = {to_id, ready_reconnect, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY_RECONNECT);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY_RECONNECT}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY_RECONNECT, data);
  }

  public async emitAudioCallCustomerReadyReconnect(to_id: number, ready_reconnect: boolean, order_id: number): Promise<AudioCallReliableSendResult> {
    const data = {to_id, ready_reconnect, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT);
    return await this.emitAudioCallReliableEvent(
      SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT,
      data,
      {
        attempts: this.reliableEventMaxAttempts,
        delayMs: this.reliableEventRetryDelayMs,
        receiptTimeoutMs: this.deliveryReceiptTimeoutMs,
      },
    );
  }

  public emitAudioCallDeliveryReceipt(meta: AudioCallReliableMeta, status: AudioCallDeliveryStatus, error?: string): void {
    if (!meta?.from_id || !meta?.message_id || !meta?.event) return;

    const data: {
      to_id: number;
      message_id: string;
      event: AudioCallReliableEventName;
      status: AudioCallDeliveryStatus;
      order_id?: number;
      source_socket_id?: string;
      error?: string;
    } = {
      to_id: meta.from_id,
      message_id: meta.message_id,
      event: meta.event,
      status,
    };

    if (typeof meta.order_id === 'number') {
      data.order_id = meta.order_id;
    }
    if (typeof meta.source_socket_id === 'string' && meta.source_socket_id.trim().length > 0) {
      data.source_socket_id = meta.source_socket_id.trim();
    }
    if (typeof error === 'string' && error.length > 0) {
      data.error = error;
    }

    this.logAudioCallReliableStep(meta.event, 'send_receipt', {
      message_id: data.message_id,
      to_id: data.to_id,
      order_id: data.order_id ?? null,
      source_socket_id: data.source_socket_id ?? null,
      receipt_status: data.status,
      receipt_error: data.error ?? null,
    });

    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_DELIVERY_RECEIPT);
    this.socket.emit(SOCKET_EVENT_NAMES.AUDIO_CALL_DELIVERY_RECEIPT, data);
  }

  public emitAvatarZoom(to_id: number, zoom: number): void {
    const data = {to_id, zoom};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_ZOOM);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_ZOOM}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_ZOOM, data);
  }

  public emitAvatarStats(to_id: number, bitrate: number): void {
    const data = {to_id, bitrate};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_STATS);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_STATS}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_STATS, data);
  }

  public emitEventForDev(to_id: number, data: any): void { // developer-only
    this.validateEmitData(data, SOCKET_EVENT_NAMES.EVENT_FOR_DEV);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.EVENT_FOR_DEV}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.EVENT_FOR_DEV, {to_id, data});
  }

  public emitAvatarSaveData(to_id: number, order_id: number): void {
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_SAVE_DATA);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_SAVE_DATA}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_SAVE_DATA, data);
  }

  public avatarWebrtcStatus(order_id: number, is_connected: boolean): void { // start/pause/continue ordering time
    const data = {order_id, is_connected};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_WEBRTC_STATUS);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_WEBRTC_STATUS}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_WEBRTC_STATUS, data);
  }

  public audioCallWebrtcStatus(order_id: number, is_connected: boolean): void {
    const data = {order_id, is_connected};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_WEBRTC_STATUS);
    if (!this.socketConnected || !this.isSocketInitedSubject.value) {
      this.pendingAudioCallWebrtcStatus = {order_id, is_connected};
      return;
    }
    if (
      this.pendingAudioCallWebrtcStatus?.order_id === order_id
      && this.pendingAudioCallWebrtcStatus.is_connected === is_connected
    ) {
      this.pendingAudioCallWebrtcStatus = null;
    }
    this.socket.emit(SOCKET_EVENT_NAMES.AUDIO_CALL_WEBRTC_STATUS, data);
  }

  public hasPendingAudioCallWebrtcStatus(orderId?: number | null): boolean {
    if (!this.pendingAudioCallWebrtcStatus) return false;
    if (typeof orderId !== 'number' || !Number.isFinite(orderId)) return true;
    return this.pendingAudioCallWebrtcStatus.order_id === orderId;
  }

  public wasSocketInitializedRecently(windowMs: number): boolean {
    if (!Number.isFinite(windowMs) || windowMs <= 0) return false;
    if (!this.lastSocketInitAt) return false;
    return (Date.now() - this.lastSocketInitAt) <= windowMs;
  }

  public emitAvatarViewerReady(to_id: number, order_id: number): void {
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_VIEWER_READY);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_VIEWER_READY}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_VIEWER_READY, data);
  }

  public emitAvatarStreamerReady(to_id: number, order_id: number): void {
    const data = {to_id, order_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.AVATAR_STREAMER_READY);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.AVATAR_STREAMER_READY}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.AVATAR_STREAMER_READY, data);
  }

  public joinStreamRoom(video_id: number): void {
    const data = {video_id};
    this.validateEmitData(video_id, SOCKET_EVENT_NAMES.JOIN_STREAM_ROOM);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.JOIN_STREAM_ROOM}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.JOIN_STREAM_ROOM, data);
  }

  public leaveStreamRoom(video_id: number): void {
    const data = {video_id};
    this.validateEmitData(video_id, SOCKET_EVENT_NAMES.LEAVE_STREAM_ROOM);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.LEAVE_STREAM_ROOM}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.LEAVE_STREAM_ROOM, data);
  }

  public emitStreamMessage(video_id: number, message: string): void {
    const data = {video_id, message};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.STREAM_MESSAGE);
    // console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.STREAM_MESSAGE}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.STREAM_MESSAGE, data);
  }

  public emitStreamBanUser(video_id: number, user_id: number): void {
    const data: StreamBanUserInfo = {video_id, user_id};
    this.validateEmitData(data, SOCKET_EVENT_NAMES.STREAM_BAN_USER);
    console.log('\x1b[35m' + `Socket emit '${SOCKET_EVENT_NAMES.STREAM_BAN_USER}'` + '\x1b[0m', data);
    this.socket.emit(SOCKET_EVENT_NAMES.STREAM_BAN_USER, data);
  }

  //#endregion Socket Emits

  public socketConnect(): void {
    if (this.socketWasReplaced) return;

    this.socketConnectionRequested = true;
    if (
      this.onlineStateService.isOffline
      || this.socketInitReconnectTimeout
      || this.socketInitAuthRefreshPromise
    ) return;
    if (this.socketConnected) {
      if (this.authService.isAuthenticated) {
        void this.initJwtToken();
      }
      return;
    }

    this.socket.connect();
  }

  public socketDisconnect(): void {
    this.socketConnectionRequested = false;
    this.clearAudioCallReceiptState();
    this.clearSocketInitState();
    this.socketInitPayloadMode = 'structured';
    this.clearSocketInitReconnectTimeout();
    this.socketInitAuthRefreshAttempts = 0;
    this.isSocketConnectSubject.next(false);
    this.isSocketInitedSubject.next(false);
    this.lastSocketInitAt = 0;
    this.socket.disconnect();
  }

  private isAudioCallDeliveryStatus(status: unknown): status is AudioCallDeliveryStatus {
    return status === 'processed'
      || status === 'duplicate'
      || status === 'ignored'
      || status === 'failed';
  }

  private parseAudioCallReliableEventName(eventName: unknown): AudioCallReliableEventName | null {
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT) return eventName;
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT) return eventName;
    return null;
  }

  private createReliableMessageId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }

  private clearAudioCallReceiptState(): void {
    this.pendingAudioCallReceiptResolvers.clear();
    this.cachedAudioCallReceipts.clear();
  }

  private enqueuePendingAudioCallCandidate(to_id: number, candidate: RTCIceCandidateInit, message_id: string): void {
    const now: number = Date.now();
    this.cleanupPendingAudioCallCandidates(now);

    const candidateValue: unknown = Reflect.get(candidate, 'candidate');
    const sdpMid: unknown = Reflect.get(candidate, 'sdpMid');
    const sdpMLineIndex: unknown = Reflect.get(candidate, 'sdpMLineIndex');
    const orderId: unknown = Reflect.get(candidate, 'order_id');

    const hasDuplicate: boolean = this.pendingAudioCallCandidates.some((item): boolean => {
      return item.to_id === to_id
        && Reflect.get(item.candidate, 'candidate') === candidateValue
        && Reflect.get(item.candidate, 'sdpMid') === sdpMid
        && Reflect.get(item.candidate, 'sdpMLineIndex') === sdpMLineIndex
        && Reflect.get(item.candidate, 'order_id') === orderId;
    });
    if (hasDuplicate) return;

    this.pendingAudioCallCandidates.push({
      to_id,
      candidate,
      queuedAt: now,
      message_id,
    });
    if (this.pendingAudioCallCandidates.length > this.maxPendingAudioCallCandidates) {
      this.pendingAudioCallCandidates.shift();
    }
  }

  private cleanupPendingAudioCallCandidates(now: number = Date.now()): void {
    const ttlMs: number = this.pendingAudioCallCandidateTtlMs;
    this.pendingAudioCallCandidates = this.pendingAudioCallCandidates.filter((item): boolean => (now - item.queuedAt) <= ttlMs);
  }

  private flushPendingAudioCallCandidates(): void {
    if (!this.pendingAudioCallCandidates.length) return;
    if (!this.socketConnected || !this.isSocketInitedSubject.value) return;

    this.cleanupPendingAudioCallCandidates();
    if (!this.pendingAudioCallCandidates.length) return;

    const pending = [...this.pendingAudioCallCandidates];
    this.pendingAudioCallCandidates = [];

    pending.forEach((item): void => {
      this.emitQueuedAudioCallCandidateReliably(item.to_id, item.candidate, item.message_id);
    });
  }

  private flushPendingAudioCallWebrtcStatus(): void {
    if (!this.pendingAudioCallWebrtcStatus) return;
    if (!this.socketConnected || !this.isSocketInitedSubject.value) return;

    const pendingStatus: PendingAudioCallWebrtcStatus = this.pendingAudioCallWebrtcStatus;
    this.pendingAudioCallWebrtcStatus = null;
    this.audioCallWebrtcStatus(pendingStatus.order_id, pendingStatus.is_connected);
  }

  private emitQueuedAudioCallCandidateReliably(to_id: number, candidate: RTCIceCandidateInit, messageId: string): void {
    void this.emitAudioCallReliableEvent(
      SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE,
      {to_id, candidate},
      {
        attempts: 3,
        delayMs: this.reliableEventRetryDelayMs,
        receiptTimeoutMs: this.deliveryReceiptTimeoutMs,
        messageId,
        ignoredReceiptRetryable: true,
      },
    ).then((): void => undefined);
  }

  private buildAudioCallReceiptKey(messageId: string, eventName: AudioCallReliableEventName, fromId: number): string {
    return `${messageId}::${eventName}::${fromId}`;
  }

  private cleanupCachedAudioCallReceipts(now: number): void {
    this.cachedAudioCallReceipts.forEach((entry: CachedAudioCallReceiptEntry, cacheKey: string): void => {
      if (entry.expiresAt > now) return;
      this.cachedAudioCallReceipts.delete(cacheKey);
    });
  }

  private getAudioCallDeliveryStatusPriority(status: AudioCallDeliveryStatus): number {
    if (status === 'processed') return 4;
    if (status === 'duplicate') return 3;
    if (status === 'failed') return 2;
    if (status === 'ignored') return 1;
    return 0;
  }

  private isSuccessfulAudioCallDeliveryStatus(status: AudioCallDeliveryStatus): boolean {
    return status === 'processed' || status === 'duplicate';
  }

  private getCachedAudioCallReceipt(cacheKey: string, minReceivedAt: number): AudioCallDeliveryReceipt | null {
    const cachedEntry: CachedAudioCallReceiptEntry | undefined = this.cachedAudioCallReceipts.get(cacheKey);
    if (!cachedEntry) return null;

    if (cachedEntry.receivedAt < minReceivedAt && !this.isSuccessfulAudioCallDeliveryStatus(cachedEntry.receipt.status)) {
      return null;
    }

    return cachedEntry.receipt;
  }

  private cacheAudioCallReceipt(receipt: AudioCallDeliveryReceipt): void {
    const now: number = Date.now();
    const cacheKey: string = this.buildAudioCallReceiptKey(receipt.message_id, receipt.event, receipt.from_id);
    this.cleanupCachedAudioCallReceipts(now);

    const previousEntry: CachedAudioCallReceiptEntry | undefined = this.cachedAudioCallReceipts.get(cacheKey);
    const previousReceipt: AudioCallDeliveryReceipt | undefined = previousEntry?.receipt;
    const shouldReplaceCachedReceipt: boolean = !previousReceipt
      || this.getAudioCallDeliveryStatusPriority(receipt.status) >= this.getAudioCallDeliveryStatusPriority(previousReceipt.status);

    const nextEntry: CachedAudioCallReceiptEntry = shouldReplaceCachedReceipt
      ? {
        receipt,
        receivedAt: now,
        expiresAt: now + this.receiptCacheTtlMs,
      }
      : {
        receipt: previousEntry!.receipt,
        receivedAt: previousEntry!.receivedAt,
        expiresAt: now + this.receiptCacheTtlMs,
      };
    this.cachedAudioCallReceipts.set(cacheKey, nextEntry);
  }

  private resolveAudioCallDeliveryReceipt(receipt: AudioCallDeliveryReceipt): void {
    this.logAudioCallReliableStep(receipt.event, 'incoming_receipt', {
      message_id: receipt.message_id,
      from_id: receipt.from_id,
      to_id: receipt.to_id,
      status: receipt.status,
      error: receipt.error ?? null,
    });
    this.cacheAudioCallReceipt(receipt);
    const cacheKey: string = this.buildAudioCallReceiptKey(receipt.message_id, receipt.event, receipt.from_id);

    const resolvers: Array<(value: AudioCallDeliveryReceipt) => void> | undefined = this.pendingAudioCallReceiptResolvers.get(cacheKey);
    if (!resolvers?.length) return;

    [...resolvers].forEach((resolve): void => resolve(receipt));
  }

  private async waitAudioCallDeliveryReceipt(
    messageId: string,
    eventName: AudioCallReliableEventName,
    expectedFromId: number,
    timeoutMs: number,
    minReceivedAt: number,
  ): Promise<AudioCallDeliveryReceipt | null> {
    if (!messageId) return null;
    if (!expectedFromId || !Number.isFinite(expectedFromId)) return null;

    const now: number = Date.now();
    const cacheKey: string = this.buildAudioCallReceiptKey(messageId, eventName, expectedFromId);
    this.cleanupCachedAudioCallReceipts(now);

    const cachedReceipt: AudioCallDeliveryReceipt | null = this.getCachedAudioCallReceipt(cacheKey, minReceivedAt);
    if (cachedReceipt) {
      return cachedReceipt;
    }

    return await new Promise((resolve): void => {
      let isSettled: boolean = false;
      let timeout: ReturnType<typeof setTimeout>;
      let resolver: (value: AudioCallDeliveryReceipt) => void = (): void => undefined;
      let latestReceipt: AudioCallDeliveryReceipt | null = null;

      const saveLatestReceipt = (nextReceipt: AudioCallDeliveryReceipt): void => {
        if (!latestReceipt) {
          latestReceipt = nextReceipt;
          return;
        }

        if (this.getAudioCallDeliveryStatusPriority(nextReceipt.status) >= this.getAudioCallDeliveryStatusPriority(latestReceipt.status)) {
          latestReceipt = nextReceipt;
        }
      };

      const resolveByLatestReceipt = (): AudioCallDeliveryReceipt | null => {
        const cachedReceipt: AudioCallDeliveryReceipt | null = this.getCachedAudioCallReceipt(cacheKey, minReceivedAt);
        if (!cachedReceipt) return latestReceipt;
        if (!latestReceipt) return cachedReceipt;

        return this.getAudioCallDeliveryStatusPriority(cachedReceipt.status) >= this.getAudioCallDeliveryStatusPriority(latestReceipt.status)
          ? cachedReceipt
          : latestReceipt;
      };

      const removeResolver = (resolver: (value: AudioCallDeliveryReceipt) => void): void => {
        const latestResolvers: Array<(value: AudioCallDeliveryReceipt) => void> = this.pendingAudioCallReceiptResolvers.get(cacheKey) ?? [];
        if (!latestResolvers.length) {
          this.pendingAudioCallReceiptResolvers.delete(cacheKey);
          return;
        }

        const nextResolvers: Array<(value: AudioCallDeliveryReceipt) => void> = latestResolvers.filter((item): boolean => item !== resolver);
        if (nextResolvers.length) {
          this.pendingAudioCallReceiptResolvers.set(cacheKey, nextResolvers);
        } else {
          this.pendingAudioCallReceiptResolvers.delete(cacheKey);
        }
      };

      const finalize = (receipt: AudioCallDeliveryReceipt | null): void => {
        if (isSettled) return;
        isSettled = true;
        clearTimeout(timeout);
        removeResolver(resolver);
        resolve(receipt);
      };

      resolver = (receipt: AudioCallDeliveryReceipt): void => {
        saveLatestReceipt(receipt);
        if (this.isSuccessfulAudioCallDeliveryStatus(receipt.status)) {
          finalize(receipt);
        }
      };
      const currentResolvers: Array<(value: AudioCallDeliveryReceipt) => void> = this.pendingAudioCallReceiptResolvers.get(cacheKey) ?? [];
      currentResolvers.push(resolver);
      this.pendingAudioCallReceiptResolvers.set(cacheKey, currentResolvers);

      timeout = setTimeout((): void => {
        finalize(resolveByLatestReceipt());
      }, timeoutMs);
    });
  }

  private async waitDelay(delayMs: number): Promise<void> {
    await new Promise((resolve): void => {
      setTimeout(resolve, delayMs);
    });
  }

  private buildReliableSendResult(
    message_id: string,
    ack: SocketDeliveryAck | null,
    receipt: AudioCallDeliveryReceipt | null,
    delivered: boolean,
    retryable: boolean,
    status: string,
    message: string,
  ): AudioCallReliableSendResult {
    return {
      message_id,
      ack,
      receipt,
      delivered,
      retryable,
      status,
      message,
    };
  }

  private logAudioCallReliableResult(
    eventName: AudioCallReliableEventName,
    result: AudioCallReliableSendResult,
    attempt: number,
    maxAttempts: number,
  ): void {
    this.audioCallConnectionDebugService.traceReliableResult(eventName, result);
  }

  private logAudioCallReliableStep(
    eventName: AudioCallReliableEventName,
    step: string,
    payload: { [key: string]: unknown },
  ): void {
    this.audioCallConnectionDebugService.traceReliableStep(eventName, step, payload);
  }

  private getSubjectObserverCount(subject: Subject<unknown>): number {
    const observers: unknown = Reflect.get(subject, 'observers');
    return Array.isArray(observers) ? observers.length : 0;
  }

  private logAudioCallEventDispatch(eventName: AudioCallReliableEventName, meta: AudioCallReliableMeta | null): void {
    const observerCount: number = this.getSubjectObserverCount(this.audioCallEvent);
    const payload: { [key: string]: unknown } = {
      message_id: meta?.message_id ?? null,
      order_id: meta?.order_id ?? null,
      from_id: meta?.from_id ?? null,
      to_id: meta?.to_id ?? null,
      source_socket_id: meta?.source_socket_id ?? null,
      observer_count: observerCount,
      auth_user_id: this.authService.parsedUser?.id ?? null,
      socket_connected: this.socketConnected,
      socket_inited: this.isSocketInitedSubject.value,
    };

    this.logAudioCallReliableStep(eventName, 'dispatch_event', payload);
  }

  private isAudioCallReliableEnvelope(rawData: unknown, eventName: AudioCallReliableEventName): rawData is AudioCallReliableEnvelope {
    if (!rawData || typeof rawData !== 'object') return false;

    const messageId: unknown = Reflect.get(rawData, 'message_id');
    const rawEvent: unknown = Reflect.get(rawData, 'event');
    const fromId: unknown = Reflect.get(rawData, 'from_id');
    const toId: unknown = Reflect.get(rawData, 'to_id');
    const payload: unknown = Reflect.get(rawData, 'payload');

    return typeof messageId === 'string'
      && messageId.trim().length > 0
      && rawEvent === eventName
      && typeof fromId === 'number'
      && Number.isFinite(fromId)
      && typeof toId === 'number'
      && Number.isFinite(toId)
      && payload !== undefined;
  }

  private extractAudioCallPayload(eventName: AudioCallReliableEventName, rawData: unknown): { payload: any; meta: AudioCallReliableMeta | null } {
    if (!this.isAudioCallReliableEnvelope(rawData, eventName)) {
      return {payload: rawData, meta: null};
    }

    const messageIdRaw: unknown = Reflect.get(rawData, 'message_id');
    const fromIdRaw: unknown = Reflect.get(rawData, 'from_id');
    const toIdRaw: unknown = Reflect.get(rawData, 'to_id');
    const orderIdRaw: unknown = Reflect.get(rawData, 'order_id');
    const sourceSocketIdRaw: unknown = Reflect.get(rawData, 'source_socket_id');
    const payload: unknown = Reflect.get(rawData, 'payload');

    const meta: AudioCallReliableMeta = {
      message_id: typeof messageIdRaw === 'string' ? messageIdRaw : '',
      event: eventName,
      from_id: typeof fromIdRaw === 'number' ? fromIdRaw : 0,
      to_id: typeof toIdRaw === 'number' ? toIdRaw : 0,
    };
    if (typeof orderIdRaw === 'number' && Number.isFinite(orderIdRaw)) {
      meta.order_id = orderIdRaw;
    }
    if (typeof sourceSocketIdRaw === 'string' && sourceSocketIdRaw.trim().length > 0) {
      meta.source_socket_id = sourceSocketIdRaw.trim();
    }

    this.logAudioCallReliableStep(eventName, 'incoming_event', {
      message_id: meta.message_id,
      from_id: meta.from_id,
      to_id: meta.to_id,
      order_id: meta.order_id ?? null,
      source_socket_id: meta.source_socket_id ?? null,
    });
    this.audioCallConnectionDebugService.traceIncomingReliableEvent(eventName, {
      message_id: meta.message_id,
      from_id: meta.from_id,
      to_id: meta.to_id,
      order_id: meta.order_id ?? null,
      source_socket_id: meta.source_socket_id ?? null,
    });

    return {payload, meta};
  }

  private async emitAudioCallReliableEvent(
    eventName: AudioCallReliableEventName,
    eventData: { to_id: number; order_id?: number; [key: string]: unknown },
    options?: {
      attempts?: number;
      ackTimeoutMs?: number;
      delayMs?: number;
      receiptTimeoutMs?: number;
      messageId?: string;
      ignoredReceiptRetryable?: boolean;
      waitForReceipt?: boolean;
      acceptAckDeliveryWithoutReceipt?: boolean;
    },
  ): Promise<AudioCallReliableSendResult> {
    const initialMessageId: string = typeof options?.messageId === 'string' && options.messageId.trim().length > 0
      ? options.messageId.trim()
      : this.createReliableMessageId();
    const maxAttempts: number = typeof options?.attempts === 'number' && options.attempts > 0
      ? Math.floor(options.attempts)
      : 1;
    const ackTimeoutMs: number = typeof options?.ackTimeoutMs === 'number' && options.ackTimeoutMs > 0
      ? Math.floor(options.ackTimeoutMs)
      : this.ackTimeoutMs;
    const delayMs: number = typeof options?.delayMs === 'number' && options.delayMs >= 0
      ? Math.floor(options.delayMs)
      : this.reliableEventRetryDelayMs;
    const receiptTimeoutMs: number = typeof options?.receiptTimeoutMs === 'number' && options.receiptTimeoutMs > 0
      ? Math.floor(options.receiptTimeoutMs)
      : this.deliveryReceiptTimeoutMs;
    const ignoredReceiptRetryable: boolean = options?.ignoredReceiptRetryable === true;
    const waitForReceipt: boolean = options?.waitForReceipt !== false;
    const acceptAckDeliveryWithoutReceipt: boolean = options?.acceptAckDeliveryWithoutReceipt === true;
    const expectedReceiptFromId: number = Number(eventData.to_id);
    const finalizeResult = (
      ack: SocketDeliveryAck | null,
      receipt: AudioCallDeliveryReceipt | null,
      delivered: boolean,
      retryable: boolean,
      status: string,
      message: string,
      attempt: number,
    ): AudioCallReliableSendResult => {
      const result: AudioCallReliableSendResult = this.buildReliableSendResult(
        initialMessageId,
        ack,
        receipt,
        delivered,
        retryable,
        status,
        message,
      );
      this.logAudioCallReliableResult(eventName, result, attempt, maxAttempts);
      return result;
    };

    let lastAck: SocketDeliveryAck | null = null;
    let lastReceipt: AudioCallDeliveryReceipt | null = null;

    for (let attempt: number = 1; attempt <= maxAttempts; attempt += 1) {
      const attemptStartedAt: number = Date.now();
      this.logAudioCallReliableStep(eventName, 'send_attempt', {
        message_id: initialMessageId,
        attempt,
        maxAttempts,
        to_id: eventData.to_id,
      });

      if (!this.socketConnected || !this.isSocketInitedSubject.value) {
        this.logAudioCallReliableStep(eventName, 'send_blocked_socket_not_ready', {
          message_id: initialMessageId,
          attempt,
          maxAttempts,
        });
        if (attempt >= maxAttempts) {
          return finalizeResult(
            lastAck,
            lastReceipt,
            false,
            true,
            'socket_not_ready',
            'Socket is not connected or not initialized',
            attempt,
          );
        }
        await this.waitDelay(delayMs);
        continue;
      }

      const reliableEventData = {
        ...eventData,
        message_id: initialMessageId,
      };

      lastAck = await this.emitWithAck<SocketDeliveryAck>(
        eventName,
        reliableEventData,
        ackTimeoutMs,
      );
      this.logAudioCallReliableStep(eventName, 'ack_result', {
        message_id: initialMessageId,
        attempt,
        maxAttempts,
        ack_received: !!lastAck,
        ack_status: lastAck?.status ?? null,
        ack_code: lastAck?.code ?? null,
        ack_delivered: lastAck?.delivered ?? null,
        ack_retryable: lastAck?.retryable ?? null,
      });

      if (!waitForReceipt) {
        if (lastAck && lastAck.delivered === false && lastAck.retryable === false) {
          return finalizeResult(
            lastAck,
            null,
            false,
            false,
            lastAck.status || 'rejected',
            lastAck.message || `${eventName} was rejected`,
            attempt,
          );
        }

        if (lastAck && lastAck.delivered === false && lastAck.retryable === true) {
          if (attempt < maxAttempts) {
            await this.waitDelay(delayMs);
            continue;
          }

          return finalizeResult(
            lastAck,
            null,
            false,
            true,
            lastAck.status || 'not_delivered',
            lastAck.message || `${eventName} is not delivered`,
            attempt,
          );
        }

        if (lastAck && lastAck.delivered === true) {
          return finalizeResult(
            lastAck,
            null,
            true,
            false,
            lastAck.status || 'delivered',
            lastAck.message || `Event '${eventName}' was delivered by socket ack`,
            attempt,
          );
        }

        if (attempt < maxAttempts) {
          await this.waitDelay(delayMs);
          continue;
        }

        return finalizeResult(
          lastAck,
          null,
          false,
          true,
          'ack_timeout',
          `No socket ack for event '${eventName}'`,
          attempt,
        );
      }

      if (lastAck && lastAck.delivered === false && lastAck.retryable === false) {
        return finalizeResult(
          lastAck,
          null,
          false,
          false,
          lastAck.status || 'rejected',
          lastAck.message || `${eventName} was rejected`,
          attempt,
        );
      }

      lastReceipt = await this.waitAudioCallDeliveryReceipt(
        initialMessageId,
        eventName,
        expectedReceiptFromId,
        receiptTimeoutMs,
        attemptStartedAt,
      );
      this.logAudioCallReliableStep(eventName, 'receipt_result', {
        message_id: initialMessageId,
        attempt,
        maxAttempts,
        receipt_received: !!lastReceipt,
        receipt_status: lastReceipt?.status ?? null,
        receipt_error: lastReceipt?.error ?? null,
      });
      if (lastReceipt) {
        if (lastReceipt.status === 'processed' || lastReceipt.status === 'duplicate') {
          return finalizeResult(
            lastAck,
            lastReceipt,
            true,
            false,
            lastReceipt.status,
            `Event '${eventName}' was processed by receiver`,
            attempt,
          );
        }

        if (lastReceipt.status === 'ignored') {
          if (ignoredReceiptRetryable && attempt < maxAttempts) {
            await this.waitDelay(delayMs);
            continue;
          }

          return finalizeResult(
            lastAck,
            lastReceipt,
            false,
            ignoredReceiptRetryable,
            lastReceipt.status,
            `Event '${eventName}' was ignored by receiver`,
            attempt,
          );
        }

        if (lastReceipt.status === 'failed') {
          if (attempt < maxAttempts) {
            await this.waitDelay(delayMs);
            continue;
          }

          return finalizeResult(
            lastAck,
            lastReceipt,
            false,
            true,
            lastReceipt.status,
            `Event '${eventName}' failed on receiver`,
            attempt,
          );
        }
      }

      if (lastAck && lastAck.delivered === false && lastAck.retryable === true) {
        if (attempt < maxAttempts) {
          await this.waitDelay(delayMs);
          continue;
        }

        return finalizeResult(
          lastAck,
          null,
          false,
          true,
          lastAck.status || 'not_delivered',
          lastAck.message || `${eventName} is not delivered`,
          attempt,
        );
      }

      if (!lastAck) {
        if (attempt < maxAttempts) {
          await this.waitDelay(delayMs);
          continue;
        }

        return finalizeResult(
          lastAck,
          lastReceipt,
          false,
          true,
          'ack_timeout',
          `No socket ack for event '${eventName}'`,
          attempt,
        );
      }

      if (lastAck && lastAck.delivered === true && acceptAckDeliveryWithoutReceipt) {
        return finalizeResult(
          lastAck,
          null,
          true,
          false,
          lastAck.status || 'delivered',
          lastAck.message || `Event '${eventName}' was delivered by socket ack`,
          attempt,
        );
      }

      if (attempt < maxAttempts) {
        await this.waitDelay(delayMs);
      }
    }

    return finalizeResult(
      lastAck,
      lastReceipt,
      false,
      true,
      'receipt_timeout',
      `No delivery receipt for event '${eventName}'`,
      maxAttempts,
    );
  }

  private emitWithAck<TResponse>(
    eventName: SOCKET_EVENT_NAMES,
    eventData: Object,
    timeoutMs: number,
  ): Promise<TResponse | null> {
    return new Promise((resolve): void => {
      if (!this.socketConnected) {
        if (!environment.production) {
          console.warn(`Socket emit '${eventName}' skipped: socket is disconnected`, { eventData });
        }
        resolve(null);
        return;
      }

      let isSettled: boolean = false;
      const timeout: ReturnType<typeof setTimeout> = setTimeout((): void => {
        if (isSettled) return;
        isSettled = true;
        if (!environment.production) {
          console.warn(`Socket emit '${eventName}' ack timeout`, { eventData });
        }
        resolve(null);
      }, timeoutMs);

      try {
        this.socket.emit(eventName, eventData, (response: TResponse): void => {
          if (isSettled) return;
          isSettled = true;
          clearTimeout(timeout);
          resolve(response ?? null);
        });
      } catch (error) {
        if (isSettled) return;
        isSettled = true;
        clearTimeout(timeout);
        if (!environment.production) {
          console.warn(`Socket emit '${eventName}' ack failed`, error);
        }
        resolve(null);
      }
    });
  }

  private async initJwtToken(): Promise<void> {
    if (this.onlineStateService.isOffline) return;
    if (!this.socketConnected) return;
    if (this.socketInitAuthRefreshPromise) return;

    if (!this.authService.isAuthenticated) {
      const message: string = 'Method initJwtToken: User is not authenticated!';
      this.emitEventForDev(this.authService.parsedToken?.userId, {message});
      return console.warn(message);
    }

    const socketId: string = this.socketId;
    const authRevision: number = this.socketAuthRevision;
    const jwtToken: string = await this.tokenStore.getToken();

    if (
      !this.socketConnected
      || this.socketId !== socketId
      || !this.authService.isAuthenticated
      || this.socketAuthRevision !== authRevision
      || this.socketInitAuthRefreshPromise
    ) return;

    if (!jwtToken) {
      const message: string = 'initJwtToken: Token is not found!';
      this.emitEventForDev(this.authService.parsedToken?.userId, {message});
      return console.error(message);
    }

    const isTokenExpired: boolean = this.jwtService.checkIsTokenExpired(jwtToken);
    if (isTokenExpired) {
      const message: string = 'initJwtToken: Token is expired!';
      this.emitEventForDev(this.authService.parsedToken?.userId, {message});
      console.warn(message);
      this.startSocketInitAuthRefresh();
      return;
    }

    if (this.socketInitAttemptedSocketId === socketId) {
      const isCurrentAttempt: boolean = this.socketInitJwtToken === jwtToken
        && this.socketInitPending
        && this.socketInitAttemptAuthRevision === authRevision;
      const isCurrentInitializedSocket: boolean = this.socketInitJwtToken === jwtToken
        && this.isSocketInitedSubject.value;
      if (isCurrentInitializedSocket) {
        this.socketInitAttemptAuthRevision = authRevision;
        return;
      }
      if (isCurrentAttempt) return;

      this.restartSocketForInit();
      return;
    }

    this.isSocketInitedSubject.next(false);
    this.lastSocketInitAt = 0;
    this.socketInitAttemptedSocketId = socketId;
    this.socketInitJwtToken = jwtToken;
    this.socketInitAttemptAuthRevision = authRevision;
    this.socketInitPending = true;
    this.scheduleSocketInitAnswerTimeout(jwtToken, socketId);
    this.emitInit(jwtToken);
  }

  private async refreshToken(): Promise<void> {
    return this.authService.refreshToken();
  }

  private emitLegacySocketInit(jwtToken: string): void {
    this.socket.emit(SOCKET_EVENT_NAMES.INIT, jwtToken);
  }

  private scheduleSocketInitAnswerTimeout(jwtToken: string, socketId: string): void {
    this.clearSocketInitAnswerTimeout();
    this.socketInitAnswerTimeout = setTimeout((): void => {
      this.socketInitAnswerTimeout = null;
      if (
        !this.socketInitPending
        || this.socketWasReplaced
        || this.socketInitJwtToken !== jwtToken
        || this.socketInitAttemptedSocketId !== socketId
        || !this.socketConnected
        || this.socketId !== socketId
      ) return;

      if (!environment.production) {
        console.warn('Socket INIT confirmation timeout', {socketId});
      }

      this.restartSocketForInit();
    }, this.socketInitAnswerTimeoutMs);
  }

  private restartSocketForInit(delayMs: number = this.socketInitReconnectDelayMs): void {
    if (this.socketWasReplaced || this.socketInitReconnectTimeout) return;

    this.clearAudioCallReceiptState();
    this.clearSocketInitState();
    this.lastSocketInitAt = 0;
    this.isSocketConnectSubject.next(false);
    this.isSocketInitedSubject.next(false);
    this.socket.disconnect();

    if (this.onlineStateService.isOffline || !this.authService.isAuthenticated) return;

    this.scheduleSocketInitReconnect(delayMs);
  }

  private clearSocketInitState(): void {
    this.clearSocketInitAnswerTimeout();
    this.socketInitAttemptedSocketId = null;
    this.socketInitJwtToken = null;
    this.socketInitAttemptAuthRevision = 0;
    this.socketInitPending = false;
    if (this.socketInitPayloadMode === 'legacy-probe') {
      this.socketInitPayloadMode = 'structured';
    }
  }

  private clearSocketInitAnswerTimeout(): void {
    if (!this.socketInitAnswerTimeout) return;

    clearTimeout(this.socketInitAnswerTimeout);
    this.socketInitAnswerTimeout = null;
  }

  private clearSocketInitReconnectTimeout(): void {
    if (!this.socketInitReconnectTimeout) return;

    clearTimeout(this.socketInitReconnectTimeout);
    this.socketInitReconnectTimeout = null;
  }

  private startSocketInitAuthRefresh(): void {
    if (this.socketWasReplaced || this.socketInitAuthRefreshPromise) return;

    this.clearAudioCallReceiptState();
    this.clearSocketInitState();
    this.clearSocketInitReconnectTimeout();
    this.lastSocketInitAt = 0;
    this.isSocketConnectSubject.next(false);
    this.isSocketInitedSubject.next(false);
    this.socket.disconnect();

    if (!this.authService.isAuthenticated || !this.socketConnectionRequested) return;
    if (this.socketInitAuthRefreshAttempts >= this.socketInitMaxAuthRefreshAttempts) {
      console.error('Socket INIT authentication recovery attempts exhausted');
      return;
    }

    this.socketInitAuthRefreshAttempts++;
    const refreshPromise: Promise<void> = this.refreshToken();
    this.socketInitAuthRefreshPromise = refreshPromise;
    void refreshPromise.then(
      (): void => {
        this.finishSocketInitAuthRefresh(refreshPromise, true);
      },
      (error: unknown): void => {
        console.error('Socket INIT token refresh failed', error);
        this.finishSocketInitAuthRefresh(refreshPromise, false);
      },
    );
  }

  private finishSocketInitAuthRefresh(refreshPromise: Promise<void>, isSuccessful: boolean): void {
    if (this.socketInitAuthRefreshPromise !== refreshPromise) return;

    this.socketInitAuthRefreshPromise = null;
    if (!isSuccessful) {
      if (
        this.socketInitAuthRefreshAttempts < this.socketInitMaxAuthRefreshAttempts
        && !this.socketWasReplaced
        && this.socketConnectionRequested
        && this.authService.isAuthenticated
      ) {
        this.scheduleSocketInitReconnect(this.socketInitAuthRefreshRetryDelayMs);
      }
      return;
    }
    if (
      this.socketWasReplaced
      || !this.socketConnectionRequested
      || !this.authService.isAuthenticated
      || this.onlineStateService.isOffline
    ) return;

    this.socketConnect();
  }

  private tryLegacySocketInit(): boolean {
    if (
      this.socketInitPayloadMode !== 'structured'
      || !this.socketInitPending
      || !this.canRecoverCurrentSocketAuthentication()
      || !this.socketInitJwtToken
      || !this.socketInitAttemptedSocketId
    ) return false;

    this.socketInitPayloadMode = 'legacy-probe';
    this.scheduleSocketInitAnswerTimeout(this.socketInitJwtToken, this.socketInitAttemptedSocketId);
    this.emitLegacySocketInit(this.socketInitJwtToken);
    return true;
  }

  private isRejectedStructuredSocketInitError(data: SocketError): boolean {
    if (this.socketInitPayloadMode === 'structured' || data.lastMethod !== SOCKET_EVENT_NAMES.INIT) {
      return false;
    }

    const lastData: unknown = data.lastData;
    if (!lastData || typeof lastData !== 'object') return false;

    const rejectedPayload: unknown = Reflect.get(lastData, 'token');
    if (!rejectedPayload || typeof rejectedPayload !== 'object') return false;

    const rejectedToken: unknown = Reflect.get(rejectedPayload, 'token');
    return typeof rejectedToken === 'string'
      && rejectedToken === this.socketInitJwtToken;
  }

  private isSocketAuthError(code: number | undefined, message: string | undefined, error?: unknown): boolean {
    if (code === 401) return true;

    const errorText: string = `${message || ''} ${this.getSocketErrorText(error)}`.toLowerCase();
    return errorText.includes('401')
      || errorText.includes('unauthorized')
      || this.isExplicitSocketTokenError(errorText);
  }

  private isExplicitSocketTokenError(errorText: string): boolean {
    return errorText.includes('status code 401')
      || errorText.includes('unauthenticated')
      || errorText.includes('invalid credentials')
      || errorText.includes('invalid token')
      || errorText.includes('invalid jwt')
      || errorText.includes('jwt expired')
      || errorText.includes('token expired');
  }

  private getSocketErrorText(error: unknown, depth: number = 0): string {
    if (typeof error === 'string') return error;
    if (typeof error === 'number') return String(error);
    if (!error || typeof error !== 'object' || depth >= 2) return '';

    const message: unknown = Reflect.get(error, 'message');
    const nestedError: unknown = Reflect.get(error, 'error');
    const code: unknown = Reflect.get(error, 'code');
    const status: unknown = Reflect.get(error, 'status');
    return [message, nestedError, code, status]
      .map((value: unknown): string => this.getSocketErrorText(value, depth + 1))
      .join(' ');
  }

  private isSocketInitRelatedError(data: SocketError): boolean {
    return data.lastMethod === SOCKET_EVENT_NAMES.INIT || data.lastMethod === 'checkTokenExpired';
  }

  private scheduleSocketInitReconnect(delayMs: number): void {
    if (this.socketWasReplaced || this.socketInitReconnectTimeout) return;

    this.socketInitReconnectTimeout = setTimeout((): void => {
      this.socketInitReconnectTimeout = null;
      this.socketConnect();
    }, delayMs);
  }

  private canRecoverCurrentSocketAuthentication(): boolean {
    return !this.socketWasReplaced
      && this.socketConnectionRequested
      && this.authService.isAuthenticated
      && this.socketConnected
      && !!this.socketInitAttemptedSocketId
      && this.socketInitAttemptedSocketId === this.socketId
      && this.socketInitAttemptAuthRevision === this.socketAuthRevision;
  }

  private handleSocketInitAnswer(data: EventAnswer): void {
    if (
      data.type !== SOCKET_EVENT_NAMES.INIT
      || !this.socketInitPending
      || this.socketWasReplaced
      || !this.socketConnected
      || this.socketInitAttemptedSocketId !== this.socketId
      || this.socketInitAttemptAuthRevision !== this.socketAuthRevision
    ) return;

    if (data.response === true) {
      if (this.socketInitPayloadMode === 'legacy-probe') {
        this.socketInitPayloadMode = 'legacy';
      }
      this.socketInitPending = false;
      this.clearSocketInitAnswerTimeout();
      this.socketInitAuthRefreshAttempts = 0;
      this.isSocketInitedSubject.next(true);
      this.lastSocketInitAt = Date.now();
      this.flushPendingAudioCallWebrtcStatus();
      this.flushPendingAudioCallCandidates();
      return;
    }

    if (this.isSocketAuthError(undefined, data.message, data.error)) {
      if (this.tryLegacySocketInit()) return;

      console.error(`Socket event '${SOCKET_EVENT_NAMES.ANSWER}', authentication error:`, data.message, data.error);
      this.startSocketInitAuthRefresh();
      return;
    }

    if (data.response !== false) return;

    this.socketInitPending = false;
    this.clearSocketInitAnswerTimeout();
    this.isSocketInitedSubject.next(false);
    this.lastSocketInitAt = 0;
    console.error(`Socket event '${SOCKET_EVENT_NAMES.ANSWER}', error:`, data.message, data.error);
  }

  private subscribeToSocketEvents(): void {
    //logEvents

    this.socket.on(SOCKET_EVENT_NAMES.ERROR, (data: SocketError): void => {
      if (this.isRejectedStructuredSocketInitError(data)) {
        if (!environment.production) console.warn('Ignored structured socket INIT error during legacy fallback');
        return;
      }

      const errorText: string = data.message?.toLowerCase() || '';
      const isInitRelatedError: boolean = this.isSocketInitRelatedError(data);
      const isSocketInitAuthError: boolean = isInitRelatedError
        && this.isSocketAuthError(data.code, data.message);
      const hasExplicitSocketTokenError: boolean = !isInitRelatedError
        && this.isExplicitSocketTokenError(errorText);
      if (isSocketInitAuthError || hasExplicitSocketTokenError) {
        if (this.canRecoverCurrentSocketAuthentication()) {
          if (data.lastMethod === SOCKET_EVENT_NAMES.INIT && this.tryLegacySocketInit()) {
            if (!environment.production) console.warn('Retrying socket INIT with legacy token payload');
            return;
          }

          this.startSocketInitAuthRefresh();
          if (!environment.production) console.error('Socket auth error event! ', data);
          return;
        }

        if (!environment.production) console.error('Ignored stale socket auth error event! ', data);
        return;
      }

      if (!environment.production) console.error('Socket err event! ', data);
      // if (data && data.message && data.message != 'avatar_order.broadcast_is_started') {
      //   this.toastService.error(data.message);
      // }
    });

    this.socket.on(SOCKET_EVENT_NAMES.LOG, (data: SocketError): void => {
      if (!environment.production) console.warn('Socket log event! ', data);
    });

    this.socket.on(SOCKET_EVENT_NAMES.SOCKET_REPLACED, (data: SocketReplacedEvent): void => {
      if (data.socket_id === this.socketId) return;

      this.socketWasReplaced = true;
      this.socketConnectionRequested = false;
      this.socketAuthRevision++;
      this.clearAudioCallReceiptState();
      this.clearSocketInitState();
      this.clearSocketInitReconnectTimeout();
      this.lastSocketInitAt = 0;
      this.isSocketConnectSubject.next(false);
      this.isSocketInitedSubject.next(false);
      this.socket.disconnect();
    });

    //connectEvents

    this.socket.on('connect', async (): Promise<void> => {
      if (this.socketWasReplaced || !this.socketConnectionRequested) {
        this.socket.disconnect();
        return;
      }

      this.clearSocketInitReconnectTimeout();
      console.info('\x1b[32m' + 'Socket is connected' + '\x1b[0m');
      console.info('\x1b[33m' + 'Socket ID: ' + '\x1b[0m' + this.socketId);

      this.isSocketConnectSubject.next(true);

      if (this.authService.isAuthenticated) {
        await this.initJwtToken();
      }
    });

    this.socket.on('disconnect', (reason: string): void => {
      console.info('\x1b[31m' + 'Socket is disconnected!' + '\x1b[0m' + ' Reason: ' + reason);

      this.clearAudioCallReceiptState();
      this.clearSocketInitState();
      this.lastSocketInitAt = 0;
      this.isSocketConnectSubject.next(false);
      this.isSocketInitedSubject.next(false);

      if (reason === 'io server disconnect' && !this.socketWasReplaced && this.socketConnectionRequested) {
        this.socketConnect();
      }
    });

    this.socket.on('connect_error', (error: unknown): void => {
      console.error('Socket connection error:', this.getSocketErrorText(error));
      if (this.socketWasReplaced || !this.socketConnectionRequested) return;
      if (this.onlineStateService.isOffline) {
        this.socket.disconnect();
        return;
      }

      setTimeout((): void => {
        if (!this.socketConnectionRequested || this.socketWasReplaced) return;
        this.socketConnect();
      }, 3_000);
    });

    this.socket.on(SOCKET_EVENT_NAMES.ANSWER, (data: EventAnswer): void => {
      this.handleSocketInitAnswer(data);
    });

    this.socket.on(SOCKET_EVENT_NAMES.CHAT_PRESENCE_INIT, (data: ChatPresenceState[]): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.CHAT_PRESENCE_INIT);
      this.chatPresenceEvent.next({
        type: SOCKET_EVENT_NAMES.CHAT_PRESENCE_INIT,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.CHAT_PRESENCE_UPDATE, (data: ChatPresenceUpdate): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.CHAT_PRESENCE_UPDATE);
      this.chatPresenceEvent.next({
        type: SOCKET_EVENT_NAMES.CHAT_PRESENCE_UPDATE,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.CHAT_TYPING_UPDATE, (data: ChatTypingUpdate): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.CHAT_TYPING_UPDATE);
      this.chatTypingEvent.next({
        type: SOCKET_EVENT_NAMES.CHAT_TYPING_UPDATE,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.CHAT_TYPING_SNAPSHOT, (data: ChatTypingSnapshot): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.CHAT_TYPING_SNAPSHOT);
      this.chatTypingEvent.next({
        type: SOCKET_EVENT_NAMES.CHAT_TYPING_SNAPSHOT,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.MESSAGE, (data: any): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.MESSAGE}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.MESSAGE);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.MESSAGE,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.REPLY_STATUS_CHANGED, (data: any): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.REPLY_STATUS_CHANGED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.REPLY_STATUS_CHANGED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.REPLY_STATUS_CHANGED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.TASK_CREATED, (data: number): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.TASK_CREATED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.TASK_CREATED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.TASK_CREATED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.QTY_UNREAD_MESSAGES, (data: any): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.QTY_UNREAD_MESSAGES}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.QTY_UNREAD_MESSAGES);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.QTY_UNREAD_MESSAGES,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.CHAT_CREATED, (data: EventChatCreated): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.CHAT_CREATED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.CHAT_CREATED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.CHAT_CREATED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.DATA_UPDATED, (data: any): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.DATA_UPDATED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.DATA_UPDATED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.DATA_UPDATED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.WALLET_TRANSFER_UPDATED, (data: EventWalletTransferUpdated): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.WALLET_TRANSFER_UPDATED);
      if (this.processedWalletTransferEventIds.has(data.event_id)) {
        return;
      }

      this.processedWalletTransferEventIds.add(data.event_id);
      if (this.processedWalletTransferEventIds.size > this.maxProcessedWalletTransferEvents) {
        const oldestEventId = this.processedWalletTransferEventIds.values().next().value;
        if (oldestEventId) {
          this.processedWalletTransferEventIds.delete(oldestEventId);
        }
      }

      this.walletTransferEvent.next(data);
    });

    this.socket.on(SOCKET_EVENT_NAMES.CHAT_UNREAD_UPDATED, (data: ChatUnreadUpdatedSocketEvent): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.CHAT_UNREAD_UPDATED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.CHAT_UNREAD_UPDATED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.REACTION_NAVIGATION_UPDATED, (data: any): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.REACTION_NAVIGATION_UPDATED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.REACTION_NAVIGATION_UPDATED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.CHAT_USER_UPDATED, (data: any): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.CHAT_USER_UPDATED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.CHAT_USER_UPDATED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.GET_HISTORY, (data: any): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.GET_HISTORY}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.GET_HISTORY);
      let messages = data;
      let requestId: string | null = null;
      let chatId: number | null = null;
      let meta: {
        mode?: string | null;
        hasOlder?: boolean;
        hasNewer?: boolean;
        beforeId?: string | number | null;
        afterId?: string | number | null;
        anchorId?: string | number | null;
      } | null = null;

      if (data && typeof data === 'object' && !Array.isArray(data) && 'messages' in data) {
        const payload: {
          messages?: any;
          request_id?: string;
          requestId?: string;
          chat_id?: number;
          chatId?: number;
          mode?: string;
          history_mode?: string;
          has_older?: boolean;
          hasOlder?: boolean;
          has_newer?: boolean;
          hasNewer?: boolean;
          before_id?: string | number;
          beforeId?: string | number;
          after_id?: string | number;
          afterId?: string | number;
          anchor_id?: string | number;
          anchorId?: string | number;
        } = data;
        messages = payload.messages ?? [];
        requestId = payload.request_id ?? payload.requestId ?? null;
        chatId = typeof payload.chat_id === 'number' ? payload.chat_id : (typeof payload.chatId === 'number' ? payload.chatId : null);
        meta = {
          mode: payload.mode ?? payload.history_mode ?? null,
          hasOlder: typeof payload.has_older === 'boolean' ? payload.has_older : (typeof payload.hasOlder === 'boolean' ? payload.hasOlder : undefined),
          hasNewer: typeof payload.has_newer === 'boolean' ? payload.has_newer : (typeof payload.hasNewer === 'boolean' ? payload.hasNewer : undefined),
          beforeId: payload.before_id ?? payload.beforeId ?? null,
          afterId: payload.after_id ?? payload.afterId ?? null,
          anchorId: payload.anchor_id ?? payload.anchorId ?? null,
        };
      }
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.GET_HISTORY,
        messages,
        requestId,
        chatId,
        meta
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.DELIVERED, (data: number): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.DELIVERED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.DELIVERED);
      this.chatEvent.next({
        type: 'message_' + SOCKET_EVENT_NAMES.DELIVERED,
        data // guid
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.UNDELIVERED, (data: number): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.UNDELIVERED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.UNDELIVERED);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.UNDELIVERED,
        data // guid
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.READ, (data: EventRead): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.READ}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.READ);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.READ,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.READERS, (data: any): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.READERS);
      this.chatEvent.next({
        type: SOCKET_EVENT_NAMES.READERS,
        data
      });
    });

    //notificationEvent

    this.socket.on(SOCKET_EVENT_NAMES.NOTIFICATION, (data: any): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.NOTIFICATION}' from backend` + '\x1b[0m', data);
      // this.validateReceivedData(data, SOCKET_EVENT_NAMES.NOTIFICATION);
      this.notificationEvent.next({
        type: SOCKET_EVENT_NAMES.NOTIFICATION,
        data
      });
    });

    //fileUploadEvent

    this.socket.on(SOCKET_EVENT_NAMES.FILE_UPLOADED, (data: EventFileUploaded): void => {
      console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.FILE_UPLOADED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.FILE_UPLOADED);
      this.fileUploadEvent.next({
        type: SOCKET_EVENT_NAMES.FILE_UPLOADED,
        data
      });
    });

    //avatarEvents

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS, (data: EventAvatarSyncStatus): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_STATUS_UPDATED, (data: EventAvatarStatusUpdated): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_STATUS_UPDATED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_STATUS_UPDATED);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_STATUS_UPDATED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST, (data: PhoneInfoRequestExt): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST,
        info: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_RESPONSE, (data: PhoneInfoResponseExt): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_RESPONSE}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_RESPONSE);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_RESPONSE,
        info: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_REQUEST, (data: AvatarOrderResponse): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_REQUEST}' from backend` + '\x1b[0m', data);
      // this.validateReceivedData(order, 'avatarRequest');
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_REQUEST,
        order: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_RESPONSE, (data: EventAvatarResponse): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_RESPONSE}' from backend` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_RESPONSE);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_RESPONSE,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY, (data: EventAvatarCustomerReady): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_OFFER, (data: RTCSessionDescriptionInit): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_OFFER}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_OFFER);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_OFFER,
        offer: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_ANSWER, (data: RTCSessionDescriptionInit): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_ANSWER}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_ANSWER);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_ANSWER,
        answer: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_CANDIDATE, (data: RTCIceCandidateInit): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_CANDIDATE}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_CANDIDATE);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_CANDIDATE,
        candidate: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_MESSAGE, (data: string): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_MESSAGE}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_MESSAGE);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_MESSAGE,
        data // command
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_PARTNER_CONNECTED_STATUS, (data: EventPartnerConnectedStatus): void => {
      console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_PARTNER_CONNECTED_STATUS}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_PARTNER_CONNECTED_STATUS);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_PARTNER_CONNECTED_STATUS,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_EXIT, (data: EventExit): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_EXIT}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_EXIT);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_EXIT,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_EXIT_FROM_BACKEND, (data: EventExitFromBackend): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_EXIT_FROM_BACKEND}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_EXIT_FROM_BACKEND);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_EXIT_FROM_BACKEND,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_REQUEST_RECONNECT, (data: EventAvatarRequestReconnect): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_REQUEST_RECONNECT}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_REQUEST_RECONNECT);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_REQUEST_RECONNECT,
        request: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY_RECONNECT, (data: EventAvatarCustomerReadyReconnect): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY_RECONNECT}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY_RECONNECT);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_CUSTOMER_READY_RECONNECT,
        response: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_ZOOM, (data: number): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_ZOOM}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_ZOOM);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_ZOOM,
        zoom: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_STATS, (data: number): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_STATS}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_STATS);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_STATS,
        bitrate: data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.EVENT_FOR_DEV, (data: any): void => { // developer-only
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.EVENT_FOR_DEV}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.EVENT_FOR_DEV);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.EVENT_FOR_DEV,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_VIEWER_READY, (data: EventAvatarViewerReady): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_VIEWER_READY}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_VIEWER_READY);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_VIEWER_READY,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AVATAR_STREAMER_READY, (data: EventAvatarStreamerReady): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_STREAMER_READY}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AVATAR_STREAMER_READY);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.AVATAR_STREAMER_READY,
        data
      });
    });

    //audioCallEvents

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE, (data: EventAudioCallResponse): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE);
      this.logAudioCallEventDispatch(SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE, normalized.meta);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE,
        data: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY, (data: EventAudioCallCustomerReady): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY,
        data: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER, (data: RTCSessionDescriptionInit): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER,
        offer: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER, (data: RTCSessionDescriptionInit): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER,
        answer: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE, (data: RTCIceCandidateInit): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE,
        candidate: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT, (data: EventAudioCallExit): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT);
      this.logAudioCallEventDispatch(SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT, normalized.meta);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT,
        data: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND, (data: EventAudioCallExitFromBackend): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND);
      this.logAudioCallEventDispatch(SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND, normalized.meta);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND,
        data: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT, (data: EventAudioCallRequestReconnect): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT,
        request: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT, (data: EventAudioCallCustomerReadyReconnect): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT,
        response: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_DELIVERY_RECEIPT, (data: AudioCallReliableEnvelope<{status: AudioCallDeliveryStatus; error?: string}>): void => {
      if (!data || typeof data !== 'object') return;

      const messageIdRaw: unknown = Reflect.get(data, 'message_id');
      const eventRaw: unknown = Reflect.get(data, 'event');
      const fromIdRaw: unknown = Reflect.get(data, 'from_id');
      const toIdRaw: unknown = Reflect.get(data, 'to_id');
      const orderIdRaw: unknown = Reflect.get(data, 'order_id');
      const payloadRaw: unknown = Reflect.get(data, 'payload');

      const statusRaw: unknown = payloadRaw && typeof payloadRaw === 'object'
        ? Reflect.get(payloadRaw, 'status')
        : undefined;
      const errorRaw: unknown = payloadRaw && typeof payloadRaw === 'object'
        ? Reflect.get(payloadRaw, 'error')
        : undefined;

      if (typeof messageIdRaw !== 'string' || messageIdRaw.trim().length === 0) return;
      if (typeof fromIdRaw !== 'number' || !Number.isFinite(fromIdRaw)) return;
      if (typeof toIdRaw !== 'number' || !Number.isFinite(toIdRaw)) return;
      const currentUserId: number | null = this.authService.parsedUser?.id ?? null;
      if (currentUserId && toIdRaw !== currentUserId) return;
      if (!this.isAudioCallDeliveryStatus(statusRaw)) return;
      const matchingEventName: AudioCallReliableEventName | null = this.parseAudioCallReliableEventName(eventRaw);
      if (!matchingEventName) return;

      const receipt: AudioCallDeliveryReceipt = {
        message_id: messageIdRaw,
        event: matchingEventName,
        from_id: fromIdRaw,
        to_id: toIdRaw,
        status: statusRaw,
      };
      if (typeof orderIdRaw === 'number' && Number.isFinite(orderIdRaw)) {
        receipt.order_id = orderIdRaw;
      }
      if (typeof errorRaw === 'string' && errorRaw.length > 0) {
        receipt.error = errorRaw;
      }

      this.resolveAudioCallDeliveryReceipt(receipt);
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS, (data: EventAudioCallPartnerConnectedStatus): void => {
      const normalized = this.extractAudioCallPayload(SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS, data);
      this.validateReceivedData(normalized.payload, SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS);
      this.logAudioCallEventDispatch(SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS, normalized.meta);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS,
        data: normalized.payload,
        meta: normalized.meta,
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.AUDIO_CALL_STREAMER_READY, (data: EventAudioCallStreamerReady): void => {
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.AUDIO_CALL_STREAMER_READY);
      this.audioCallEvent.next({
        type: SOCKET_EVENT_NAMES.AUDIO_CALL_STREAMER_READY,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.STREAM_MESSAGE, (data: EventStreamMessage): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.STREAM_MESSAGE}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.STREAM_MESSAGE);
      this.dispatchStreamMessageEvent(data);
    });

    this.socket.on(SOCKET_EVENT_NAMES.STREAM_BAN_APPLIED, (data: EventStreamBanApplied): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.STREAM_BAN_APPLIED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.STREAM_BAN_APPLIED);
      this.avatarEvent.next({
        type: SOCKET_EVENT_NAMES.STREAM_BAN_APPLIED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.STREAM_LIKES, (data: EventStreamLikes): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.STREAM_LIKES}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.STREAM_LIKES);
      this.streamEvent.next({
        type: SOCKET_EVENT_NAMES.STREAM_LIKES,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.STREAM_LIVE, (data: EventStreamLive): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.STREAM_LIVE}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.STREAM_LIVE);
      this.streamEvent.next({
        type: SOCKET_EVENT_NAMES.STREAM_LIVE,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.STREAM_NOTICED, (data: EventStreamNoticed): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.STREAM_NOTICED}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.STREAM_NOTICED);
      this.streamEvent.next({
        type: SOCKET_EVENT_NAMES.STREAM_NOTICED,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.STREAM_LIVE_VIEWERS, (data: EventStreamLiveViewers): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.STREAM_LIVE_VIEWERS}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.STREAM_LIVE_VIEWERS);
      this.streamEvent.next({
        type: SOCKET_EVENT_NAMES.STREAM_LIVE_VIEWERS,
        data
      });
    });

    this.socket.on(SOCKET_EVENT_NAMES.STREAM_WATCHES, (data: EventStreamWatches): void => {
      // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.STREAM_WATCHES}'` + '\x1b[0m', data);
      this.validateReceivedData(data, SOCKET_EVENT_NAMES.STREAM_WATCHES);
      this.streamEvent.next({
        type: SOCKET_EVENT_NAMES.STREAM_WATCHES,
        data
      });
    });
  }

  //#region Validate data

  private validateReceivedData(eventData: Object, eventName: SOCKET_EVENT_NAMES): void {
    if (!eventData && eventData !== 0) console.error(`Received incorrect socket data from event '${eventName}'!`);
    this.validateObjectFields(eventData, `received from event '${eventName}'`);
  }

  private dispatchStreamMessageEvent(data: EventStreamMessage): void {
    this.avatarEvent.next({
      type: SOCKET_EVENT_NAMES.STREAM_MESSAGE,
      data,
    });
  }

  private validateEmitData(eventData: Object, eventName: SOCKET_EVENT_NAMES): void {
    if (!eventName) console.error(`Incorrect event name!`);
    if (!eventData) console.error(`Incorrect sending data for the event '${eventName}'!`);
    this.validateObjectFields(eventData, `for emit event '${eventName}'`);
  }

  private validateObjectFields(obj: Object, eventDetails: string): void {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (obj[key] === undefined) {
          console.error(`Incorrect '${key}' ${eventDetails}`);
        }
      }
    }
  }

  //#endregion Validate data
}
